
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const AuthCallback = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Check for password reset/recovery flow
        if (window.location.hash) {
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const token = hashParams.get("access_token");
          const type = hashParams.get("type");
          
          // Handle recovery token (password reset)
          if (token && type === "recovery") {
            console.log("Recovery token detected");
            
            // Verify token validity
            const { data, error } = await supabase.auth.verifyOtp({
              token_hash: token,
              type: 'recovery'
            });
            
            if (error) {
              console.error("Invalid or expired token:", error);
              // Mark as expired in session storage
              sessionStorage.setItem('token_expired', 'true');
              // Redirect to forgot password with expired flag
              navigate("/auth?mode=forgotPassword&token_expired=true", { replace: true });
              return;
            }
            
            // Token is valid, redirect to password reset page
            navigate("/auth/reset-password", {
              replace: true,
              state: { isRecovery: true }
            });
            return;
          }
          
          // Check for error parameters in hash
          const errorType = hashParams.get('error');
          const errorDesc = hashParams.get('error_description');
          
          if (errorType || errorDesc) {
            // Handle expired token or other errors
            if (errorDesc?.includes('expired') || errorType === 'access_denied') {
              sessionStorage.setItem('token_expired', 'true');
              navigate("/auth?mode=forgotPassword&token_expired=true", { replace: true });
              return;
            }
          }
        }
        
        // Handle regular auth flow (non-recovery)
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          throw error;
        }
        
        if (data?.session) {
          navigate('/');
        } else {
          navigate('/auth');
        }
      } catch (error: any) {
        console.error('Auth callback error:', error);
        toast({
          title: "Authentication error",
          description: error.message || "There was a problem with authentication",
          variant: "destructive",
        });
        navigate('/auth');
      }
    };
    
    handleAuthCallback();
  }, [navigate, toast]);
  
  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4 py-12 bg-gradient-to-b from-mono-charcoal to-mono-eerieBlack">
      <div className="animate-pulse flex flex-col items-center">
        <div className="h-16 w-16 bg-mono-slateGray/20 rounded-full"></div>
        <div className="mt-6 h-5 w-52 bg-mono-slateGray/20 rounded"></div>
        <div className="mt-3 h-4 w-40 bg-mono-slateGray/20 rounded"></div>
      </div>
    </div>
  );
};

export default AuthCallback;
